import { COMPANY_COLORS } from '@/constants/colors.constant'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { WaterfallData } from '@/interfaces/factorLens.interface'
import { cardTitleStyle, simpleCharacterButtonStyle } from '@/styles/reportDashboardStyles'
import useElementDimensions from '@/utils/useElementDimensions'
import { Button, Typography } from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HC_more from 'highcharts/highcharts-more'
import React, { useEffect, useMemo, useRef } from 'react'

HC_more(Highcharts)

const FactorWaterfall: React.FC<WaterfallData> = ({ shown, toggleVisibility, factorData, colors }) => {
  const theme = useTheme()
  const { dimensions, dimensionsRef } = useElementDimensions()
  const chartRef = useRef<HighchartsReact.RefObject>(null)

  useEffect(() => {
    chartRef.current?.chart?.reflow()
  }, [dimensions])

  const chartOptions: Highcharts.Options = useMemo(() => {
    const level2 = factorData?.levels?.[2] ?? []
    const totalRisk = factorData?.levels?.[0]?.[0]?.riskContrib ?? 0

    const seriesData: Highcharts.PointOptionsObject[] = level2.map((group, i) => ({
      name: group.name,
      y: group.riskContrib,
      color: colors.barColors[i % colors.barColors.length],
      borderWidth: 0.5
    }))

    // Bridge any gap between the sum of level2 items and the actual total
    // (can esp occur with benchmark/active data due to factors outside level2)
    const level2Sum = level2.reduce((sum, g) => sum + g.riskContrib, 0)
    const residual = totalRisk - level2Sum
    if (Math.abs(residual) > 0.001) {
      seriesData.push({
        name: 'Specific Risk',
        y: residual,
        color: colors.specificSelected,
        borderWidth: 0.5
      })
    }

    seriesData.push({
      name: 'Total Risk',
      isSum: true,
      color: COMPANY_COLORS.NEUTRAL_GREY, // cwan grey bar (card text stays white via totalSelected)
      borderWidth: 0.5
    })

    return {
      chart: {
        type: 'waterfall',
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        spacing: [10, 10, 10, 10],
      },
      title: { text: undefined },
      xAxis: {
        type: 'category',
        labels: {
          style: { color: theme.textPrimary, fontSize: '14px' },
        },
        lineColor: theme.border,
        tickColor: theme.border,
      },
      yAxis: {
        title: {
          text: 'Risk Value (%)',
          style: { color: theme.textPrimary, fontSize: '14px' },
        },
        labels: {
          style: { color: theme.textPrimary, fontSize: '14px' },
          format: '{value:.2f}',
        },
        gridLineColor: theme.border,
        min: 0,
      },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}</b><br/>Risk: <b>${(this.y as number).toFixed(2)}%</b>`
        },
      },
      plotOptions: {
        waterfall: {
          lineColor: theme.textPrimary,
          lineWidth: 1,
          dashStyle: 'Dot',
          borderWidth: 0,
          pointPadding: 0.1,
          minPointLength: 1,
        },
      },
      series: [
        {
          type: 'waterfall',
          data: seriesData,
          upColor: undefined,
        },
      ],
    }
  }, [factorData, theme, colors])

  const containerStyle = {
    width: 'auto',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.cardBackground,
    marginTop: 8,
    marginRight: 8,
    marginBottom: 8,
  }

  const titleStyle = shown
    ? { ...cardTitleStyle, color: theme.textPrimary }
    : { color: theme.textSecondary, fontSize: '1rem' }

  const header = (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2, marginBottom: 2 }}>
      <Button
        style={{ ...simpleCharacterButtonStyle, position: 'absolute', left: 0, color: theme.textPrimary }}
        onClick={toggleVisibility}
      >
        {shown ? '﹀' : '︿'}
      </Button>
      <Typography style={titleStyle}>
        Factor Waterfall
      </Typography>
    </div>
  )

  if (!shown) {
    return (
      <div style={{ ...containerStyle, height: '4rem', minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {header}
      </div>
    )
  }

  return (
    <div style={{ ...containerStyle, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {header}
      <div ref={dimensionsRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{ style: { width: '100%', height: '100%' } }}
        />
      </div>
    </div>
  )
}

export default FactorWaterfall
